// PRD 11 §3.6 Image block — title + click-to-upload + showText toggle +
// optional description.
// SHCT-454: the image file is uploaded to object storage (multipart) and only
// its returned URL is stored on the block. Previously the image was inlined as a
// base64 dataUrl, which inflated the manual's JSON save body past the request-
// size limit → 413 even for sub-5MB images. Now the save body carries only URLs.
// ADM.017 / PRD v1.4: JPG/PNG only, max 5MB, ONE image per block, NO replace
// (to change it, delete the block and add a new one). A single unified failure
// message covers both wrong-format and over-size.
// The block chrome (position, drag handle, delete) is owned by ContentBlockList.

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/components/feedback/useToast';

import { useUploadImage } from '../hooks/use-manual-mutations';
import type { ContentBlock } from '../types/counseling-manual.types';
import { BLOCK_DESC_MAX, IMAGE_ACCEPT, IMAGE_TITLE_MAX, isImageFileValid } from '../utils/validate-form';

interface ImageBlockEditorProps {
  block: ContentBlock;
  disabled?: boolean;
  onChange: (patch: Partial<ContentBlock>) => void;
}

export function ImageBlockEditor({ block, disabled = false, onChange }: ImageBlockEditorProps) {
  const { t } = useTranslation('counseling-manual');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadImage();

  const handleFile = async (file: File | null) => {
    if (!file) return;
    // ADM.017 — JPG/PNG only + 5MB. Pre-check client-side for instant feedback
    // (the BE re-validates authoritatively). On any violation keep the block and
    // its existing image, and surface ONE unified message (upload errors → toast,
    // per admin-web §7.1 file-upload exception).
    if (!isImageFileValid(file)) {
      toast.error(t('uploadErrors.imageFailed'));
      return;
    }
    try {
      const { url } = await upload.mutateAsync(file);
      onChange({ dataUrl: url });
    } catch {
      toast.error(t('uploadErrors.imageFailed'));
    }
  };

  const hasImage = Boolean(block.dataUrl);
  const busy = disabled || upload.isPending;

  return (
    <>
      <input
        type="text"
        className="form-field__input"
        value={block.title}
        onChange={(e) => onChange({ title: e.target.value })}
        placeholder={t('blocks.imageTitlePlaceholder')}
        maxLength={IMAGE_TITLE_MAX}
        disabled={disabled}
        style={{ marginBottom: 6 }}
      />

      {hasImage ? (
        // ONE image per block, NO replace — static preview once set, plus the
        // prototype's hint telling the user how to change it (delete + re-add).
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: 12,
            textAlign: 'center',
            background: 'var(--muted)',
            marginBottom: 6,
          }}
        >
          <img
            src={block.dataUrl}
            alt=""
            style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 6, display: 'block', margin: '0 auto' }}
          />
          <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 6 }}>
            {t('blocks.imageReplaceHint')}
          </div>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={busy ? -1 : 0}
          aria-disabled={busy || undefined}
          onClick={() => !busy && fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (busy) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          style={{
            border: '1px dashed var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: 16,
            textAlign: 'center',
            background: 'var(--muted)',
            cursor: busy ? 'default' : 'pointer',
            marginBottom: 6,
          }}
        >
          <div style={{ fontSize: 13, color: 'var(--muted-foreground)' }}>
            {upload.isPending ? '…' : t('blocks.imageUploadHint')}
          </div>
        </div>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => {
          void handleFile(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />

      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', marginBottom: 4 }}>
        <input
          type="checkbox"
          checked={block.showText}
          onChange={(e) => onChange({ showText: e.target.checked })}
          disabled={disabled}
          style={{ width: 14, height: 14, accentColor: 'var(--info)' }}
        />
        {t('blocks.showText')}
      </label>
      {block.showText ? (
        <textarea
          className="form-field__textarea"
          style={{ minHeight: 60 }}
          value={block.text}
          onChange={(e) => onChange({ text: e.target.value })}
          placeholder={t('blocks.imageDescPlaceholder')}
          maxLength={BLOCK_DESC_MAX}
          disabled={disabled}
        />
      ) : null}
    </>
  );
}
